import Auth from './auth'
import { isValid as isValidCpf } from '@fnando/cpf'

/*
  Traduções e opções da área de banking. As chamadas HTTP ficam nas páginas, como no
  resto do app — aqui só mora o que é compartilhado entre a abertura da conta, o
  onboarding e o saldo.
*/
export default class Banking {
  static STATUS_LABELS = {
    pending_creation: 'Criando conta',
    created: 'Conta criada',
    awaiting_documents: 'Aguardando documentos',
    awaiting_approval: 'Cadastro em análise',
    active: 'Conta ativa',
    rejected: 'Cadastro recusado'
  }

  /*
    `NOT_SENT` é o estado inicial de todo documento exigido e o mais comum de aparecer na
    tela. Sem ele aqui, o chip do documento imprimia o enum cru do Asaas.
  */
  static ASAAS_STATUS_LABELS = {
    NOT_SENT: 'Não enviado',
    PENDING: 'Pendente',
    AWAITING_APPROVAL: 'Em análise',
    APPROVED: 'Aprovado',
    REJECTED: 'Recusado'
  }

  /* Documento parado esperando o titular, e não o banco. */
  static PENDING_DOCUMENT_STATUSES = ['NOT_SENT', 'REJECTED']

  /*
    Sem `bankAccountInfo`: a conta bancária não entra na aprovação, e mostrá-la aqui como
    "Pendente" fazia parecer que a abertura esperava por ela.
  */
  static ASAAS_STATUS_TITLES = {
    commercialInfo: 'Dados cadastrais',
    documentation: 'Documentos',
    general: 'Situação geral'
  }

  static TRANSACTION_STATUS_LABELS = {
    pending: 'Em processamento',
    processing: 'Em processamento',
    confirmed: 'Concluída',
    failed: 'Não realizada',
    cancelled: 'Cancelada',
    refunded: 'Estornada'
  }

  /*
    Espelha `BankingTransactionOperation` no model da API. `other` é o destino de tudo o
    que o Asaas classifica com um tipo que ainda não conhecemos — são mais de cem, e
    novos aparecem a cada produto lançado lá.
  */
  static OPERATION_LABELS = {
    pix: 'Pix',
    bill: 'Pagamento de conta',
    commission: 'Comissão',
    internal_transfer: 'Transferência',
    withdrawal: 'Saque',
    fee: 'Tarifa',
    other: 'Movimentação'
  }

  static OPERATION_FILTERS = [
    {value: '', label: 'Todas as movimentações'},
    {value: 'pix', label: 'Pix'},
    {value: 'internal_transfer', label: 'Transferências'},
    {value: 'commission', label: 'Comissões'},
    {value: 'withdrawal', label: 'Saques'},
    {value: 'bill', label: 'Pagamentos de conta'},
    {value: 'fee', label: 'Tarifas'}
  ]

  static getOperationLabel (operation) {
    return this.OPERATION_LABELS[operation] ?? 'Movimentação'
  }

  /*
    Busca de destinatário na transferência entre contas. Abaixo disso o resultado é grande
    demais para ser útil e expõe mais contas do que o titular precisa ver para escolher uma.
  */
  static TARGET_QUERY_MIN_LENGTH = 3

  // Espelha `PixAddressKeyType` no DTO de transferência da API.
  static PIX_KEY_TYPES = [
    {value: 'CPF', label: 'CPF'},
    {value: 'CNPJ', label: 'CNPJ'},
    {value: 'EMAIL', label: 'E-mail'},
    {value: 'PHONE', label: 'Celular'},
    {value: 'EVP', label: 'Chave aleatória'}
  ]

  /*
    Como cada tipo de chave é digitado.

    `mask` é o nome semântico, e cada front o traduz para o seu sistema: os apps têm máscaras
    nomeadas em `components/Inputs/MaskedInput`, o site usa blocos do Cleave. Manter o nome
    aqui evita que os três divirjam sobre o que é "um CPF".

    E-mail e chave aleatória ficam **sem máscara de propósito**: o primeiro não tem forma
    fixa, e a segunda é hexadecimal — máscara de dígitos comeria as letras dela.

    O `placeholder` importa tanto quanto a máscara: é ele que diz qual formato se espera
    antes de a pessoa começar a digitar.
  */
  static PIX_KEY_INPUTS = {
    CPF:   { mask: 'cpf',       placeholder: '000.000.000-00' },
    CNPJ:  { mask: 'cnpj',      placeholder: '00.000.000/0000-00' },
    PHONE: { mask: 'cellphone', placeholder: '(00) 00000-0000' },
    EMAIL: { mask: null,        placeholder: 'nome@exemplo.com' },
    EVP:   { mask: null,        placeholder: '00000000-0000-0000-0000-000000000000' }
  }

  /*
    Os dois tipos de chave que não têm máscara e por isso só se reconhecem pelo texto
    inteiro.

    Moram aqui, e não no validador, porque quem adivinha o tipo e quem confere o formato
    precisam concordar sobre o que é um e-mail — duas definições divergiriam na primeira vez
    que uma delas fosse ajustada. `bankingPixTransferValidator` os importa daqui.
  */
  static EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  static EVP_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

  /*
    O que uma chave de números pode ter além dos números: a pontuação das máscaras de CPF,
    CNPJ e celular. Qualquer outro caractere tira o texto da disputa.
  */
  static NUMERIC_KEY_PATTERN = /^[\d.\-/()+ ]+$/

  /**
   * Máscara e exemplo do tipo de chave escolhido.
   *
   * Sem tipo escolhido devolve o campo livre, com o texto que lista os cinco — é o estado
   * em que o formulário nasce.
   */
  static getPixKeyInput (type) {
    return this.PIX_KEY_INPUTS[type] ?? {
      mask: null,
      placeholder: 'CPF, CNPJ, e-mail, celular ou chave aleatória'
    }
  }

  /*
    Tipos de conta que o Asaas aceita no cadastro da conta de saque. São exatamente estes
    dois: qualquer outro valor volta como "Tipo de conta informado é inválido".
  */
  static BANK_ACCOUNT_TYPES = [
    {value: 'CONTA_CORRENTE', label: 'Conta corrente'},
    {value: 'CONTA_POUPANCA', label: 'Conta poupança'}
  ]

  static COMPANY_TYPES = [
    {value: 'MEI', label: 'MEI'},
    {value: 'LIMITED', label: 'Sociedade limitada (LTDA)'},
    {value: 'INDIVIDUAL', label: 'Empresário individual (EI)'},
    {value: 'ASSOCIATION', label: 'Associação'}
  ]

  /*
    Campos devolvidos por BankingAccountService.checkReadiness quando o cadastro ainda
    não tem tudo o que o Asaas exige para abrir a conta.
  */
  static MISSING_FIELD_LABELS = {
    name: 'Nome',
    email: 'E-mail',
    cnpj: 'CNPJ',
    cpf: 'CPF',
    cellphone: 'Celular',
    phone: 'Telefone',
    birth: 'Data de nascimento',
    address: 'Endereço',
    'address.route': 'Rua/avenida',
    'address.number': 'Número',
    'address.district': 'Bairro',
    'address.city': 'Cidade',
    'address.state': 'Estado',
    'address.postal_code': 'CEP'
  }

  static getStatusLabel (status) {
    return this.STATUS_LABELS[status] ?? 'Situação desconhecida'
  }

  static getAsaasStatusLabel (status) {
    return this.ASAAS_STATUS_LABELS[status] ?? status
  }

  static getMissingFieldLabel (field) {
    return this.MISSING_FIELD_LABELS[field] ?? field
  }

  static getStatusColor (status) {
    if (['active', 'APPROVED'].includes (status)) return '#44CF6C'
    if (['rejected', 'REJECTED'].includes (status)) return '#FF3939'
    return '#FF9800'
  }

  /*
    Passos da abertura, na ordem em que o titular os percorre.

    A conta para saque não é passo: pela documentação do Asaas, a aprovação depende dos dados
    comerciais e dos documentos, e a conta bancária só serve ao saque — ela é cadastrada na
    tela de saque, na hora em que faz falta.
  */
  static ONBOARDING_STEPS = [
    {label: 'Conta criada', description: 'Seus dados foram enviados ao banco'},
    {label: 'Documentos', description: 'Envie os documentos solicitados'},
    {label: 'Análise', description: 'O banco confere os seus dados'},
    {label: 'Conta ativa', description: 'Você já pode movimentar o saldo'}
  ]

  /**
   * Em que passo da abertura o titular está.
   *
   * O status local não basta: `awaiting_documents` vale tanto para documento que falta
   * enviar quanto para documento já enviado que o Asaas ainda não pôs em análise. Quem separa
   * é a situação cadastral que o Asaas devolve.
   *
   * @param status - O status local da conta.
   * @param asaasStatus - `commercialInfo`, `bankAccountInfo`, `documentation` e `general`.
   */
  static getCurrentStep (status, asaasStatus = {}) {
    if (status === 'active') return this.ONBOARDING_STEPS.length
    if (status === 'pending_creation') return 0

    // Recusa não é etapa: ela acontece na análise, e é lá que o titular lê o motivo.
    if (status === 'rejected') return 2

    const isPending = value => !value || value === 'PENDING'

    if (isPending (asaasStatus.documentation)) return 1

    return 2
  }

  /**
   * Quantos documentos já saíram das mãos do titular.
   *
   * Conta o grupo, e não o arquivo: o Asaas pede "documento de identificação" e aceita
   * quantos arquivos forem precisos dentro dele. Recusado volta a contar como pendente,
   * porque o titular precisa enviar outro.
   */
  static getDocumentProgress (documents = []) {
    const total = documents.length
    const sent = documents.filter (
      document => !this.PENDING_DOCUMENT_STATUSES.includes (document.status)
    ).length

    return { sent, total, pending: total - sent }
  }

  /* Formatos e tamanho que a API aceita, como ela mesma os informa. */
  static getUploadHint (constraints) {
    if (!constraints) return ''

    const formats = (constraints.acceptedExtensions ?? []).join (', ').toUpperCase ()

    return `${formats} · até ${constraints.maxFileSizeMb} MB`
  }

  static getCompanyType (value) {
    return this.COMPANY_TYPES.find (type => type.value === value) ?? null
  }

  static getTransactionStatusLabel (status) {
    return this.TRANSACTION_STATUS_LABELS[status] ?? 'Situação desconhecida'
  }

  /*
    `pending` e `processing` são amarelos de propósito: uma transferência aceita ainda pode
    falhar na liquidação, e pintá-la de verde antes do `confirmed` faria o titular tratar
    como concluído um dinheiro que ainda pode voltar.
  */
  static getTransactionStatusColor (status) {
    if (status === 'confirmed') return '#44CF6C'
    if (['failed', 'cancelled', 'refunded'].includes (status)) return '#FF3939'
    return '#FF9800'
  }

  static getTargetLabel (target) {
    return target.document ? `${target.name} - ${target.document}` : target.name
  }

  /*
    "0001 / 123456-7". O dígito some quando o banco não usa um, em vez de deixar um hífen
    solto no fim do número.
  */
  static getAccountNumberLabel (bankAccount) {
    if (!bankAccount) return ''

    const digit = bankAccount.accountDigit ? `-${bankAccount.accountDigit}` : ''

    return `${bankAccount.agency} / ${bankAccount.account}${digit}`
  }

  static getPixKeyType (value) {
    return this.PIX_KEY_TYPES.find (type => type.value === value) ?? null
  }

  static getPixKeyTypeLabel (value) {
    return this.getPixKeyType (value)?.label ?? value
  }

  /**
   * Pontua a chave como a máscara do tipo pontuaria.
   *
   * Existe por causa da detecção: a máscara do campo é enfeite de tela, e o que vai para o
   * diálogo de conferência é o valor guardado no formulário. Sem isto, adivinhar o tipo
   * trocava a máscara e deixava `29537995593` na confirmação — justamente onde a pontuação
   * mais serve, porque `295.379.955-93` se confere de relance.
   *
   * Só pontua o que está completo. Valor pela metade volta como veio: quem está digitando já
   * tem a máscara do campo cuidando da aparência.
   *
   * O envio não muda — `normalizePixKey` tira a pontuação antes de a chave sair.
   *
   * @param type - O tipo da chave, de `PIX_KEY_TYPES`.
   * @param key - A chave como foi digitada.
   * @returns A chave pontuada, ou ela mesma quando não há o que pontuar.
   */
  static formatPixKey (type, key) {
    const value = (key ?? '').trim ()
    const digits = value.replace (/\D/g, '')

    if (type === 'CPF' && digits.length === 11) {
      return `${digits.slice (0, 3)}.${digits.slice (3, 6)}.${digits.slice (6, 9)}`
        + `-${digits.slice (9)}`
    }

    if (type === 'CNPJ' && digits.length === 14) {
      return `${digits.slice (0, 2)}.${digits.slice (2, 5)}.${digits.slice (5, 8)}`
        + `/${digits.slice (8, 12)}-${digits.slice (12)}`
    }

    if (type === 'PHONE' && digits.length === 11) {
      return `(${digits.slice (0, 2)}) ${digits.slice (2, 7)}-${digits.slice (7)}`
    }

    return value
  }

  /**
   * Adivinha o tipo da chave a partir do que foi digitado, para poupar o titular de
   * escolher no seletor o que a própria chave já diz.
   *
   * Não confundir com `getPixKeyType`, acima: aquele recebe o enum e devolve a opção do
   * seletor; este recebe o texto digitado e devolve o enum.
   *
   * **A ordem das regras é o desenho, e não acaso.** CPF e celular com DDD têm os mesmos
   * onze dígitos. Medindo 200 mil de cada: um celular passa no dígito verificador de CPF em
   * ~1% das vezes, e ~6,7% dos CPFs têm cara de celular. O dígito verificador é sete vezes
   * mais confiável que o formato de telefone, então ele vem primeiro — e o ramo de celular
   * só chega a ver onze dígitos que já falharam nele.
   *
   * **Só adivinha o que a máscara do tipo consegue segurar.** O palpite troca a máscara do
   * campo junto com o tipo, e uma máscara mais curta que o valor digitado apagaria o fim
   * dele — ver o comentário dos dez e treze dígitos, abaixo.
   *
   * Na dúvida devolve `null`, de propósito. `KEY_VALIDATORS`, no validador, é a única
   * conferência de formato que existe em todo o sistema: a API confere só que o tipo é um
   * dos cinco e que a chave não está vazia. Um palpite errado que ninguém corrija não
   * encontra mais nada no caminho até o Asaas.
   *
   * @param value - O texto digitado no campo da chave, com máscara ou sem.
   * @returns O valor de `PIX_KEY_TYPES`, ou `null` quando não dá para afirmar.
   */
  static guessPixKeyType (value) {
    const text = (value ?? '').trim ()

    if (!text) return null

    // Nenhum outro tipo de chave aceita arroba, e nenhum e-mail vive sem ela.
    if (text.includes ('@')) return 'EMAIL'
    if (this.EVP_PATTERN.test (text)) return 'EVP'
    if (!this.NUMERIC_KEY_PATTERN.test (text)) return null

    const digits = text.replace (/\D/g, '')

    // Nenhum telefone chega a catorze dígitos, nem com o país junto.
    if (digits.length === 14) return 'CNPJ'

    if (digits.length === 11) {
      if (isValidCpf (digits)) return 'CPF'

      /*
        Onze dígitos reprovados no DV ainda podem ser celular: todo celular brasileiro tem
        `9` depois do DDD. É a mesma regra de `Contacts.isMobile`, que os apps já usam.
      */
      return digits[2] === '9' ? 'PHONE' : null
    }

    /*
      Fixo com DDD (dez dígitos) e celular com o país junto (doze ou treze) são chave Pix
      válida para o validador, mas ficam **de fora do palpite de propósito**: o palpite traz
      a máscara do tipo junto, e nenhuma das duas caberia.

      Treze dígitos viram a máscara de celular, que para em onze — o palpite comeria o que o
      titular acabou de digitar. E uma pausa em dez dígitos, no meio de um CNPJ, prenderia
      quem digita atrás de uma máscara de onze.

      Quem usa essas duas formas escolhe o tipo no seletor, que continua ali. O Banco Central
      exige celular na chave de telefone; o fixo só chega aqui porque o validador é
      generoso.
    */
    return null
  }

  /*
    Reconhece um payload de Pix (BR Code) pela aparência: começa com o campo `00` de valor
    `01` e traz o identificador do arranjo do Banco Central. Serve para não gastar uma ida ao
    servidor quando se colou, por engano, uma chave Pix ou uma linha digitável.
  */
  static looksLikePixPayload (value) {
    const payload = (value ?? '').trim ()

    return payload.length >= 30
      && payload.startsWith ('000201')
      && payload.toLowerCase ().includes ('br.gov.bcb.pix')
  }

  /*
    Confere o CRC16 do BR Code, que é o que separa "parece um Pix" de "é um Pix".

    Os dois últimos campos de todo payload são `6304` seguido de quatro dígitos hexadecimais,
    o CRC do que veio antes. Um "copia e cola" truncado no meio — o defeito mais comum de
    quem cola de um aplicativo de mensagens — passa em `looksLikePixPayload` e reprova aqui.

    É isso que permite consultar sozinho, sem botão: só vale a pena chamar a API quando o
    código está inteiro. Quem decide de verdade continua sendo a leitura no servidor.

    Cópia fiel do mesmo método nos dois apps. Duplicado de propósito, como o resto de
    `services/Banking`: os quatro repositórios não compartilham pacote.
  */
  static isPixPayload (value) {
    const payload = (value ?? '').trim ()

    if (!this.looksLikePixPayload (payload)) return false

    const marker = payload.lastIndexOf ('6304')
    if (marker < 0 || marker + 8 !== payload.length) return false

    let crc = 0xFFFF

    for (let index = 0; index < marker + 4; index++) {
      crc ^= payload.charCodeAt (index) << 8

      for (let bit = 0; bit < 8; bit++) {
        crc = crc & 0x8000 ? ((crc << 1) ^ 0x1021) & 0xFFFF : (crc << 1) & 0xFFFF
      }
    }

    return payload.slice (marker + 4).toUpperCase ()
      === crc.toString (16).toUpperCase ().padStart (4, '0')
  }

  /*
    Põe a chave no formato que o Asaas espera antes de sair do navegador: CPF, CNPJ e
    celular sem máscara, e-mail em caixa baixa, chave aleatória como veio. O titular digita
    do jeito que leu, e o que vale para a API é o que sai daqui.
  */
  static normalizePixKey (type, key) {
    const value = (key ?? '').trim ()

    if (['CPF', 'CNPJ'].includes (type)) return value.replace (/\D/g, '')
    if (type === 'EMAIL') return value.toLowerCase ()

    if (type === 'PHONE') {
      const digits = value.replace (/\D/g, '')
      // A chave de celular vai em E.164, com ou sem o país tendo sido digitado.
      const national = digits.length > 11 && digits.startsWith ('55')
        ? digits.slice (2)
        : digits

      return `+55${national}`
    }

    return value
  }

  /*
    A conta é sempre resolvida a partir do usuário do token. Administradores são a única
    exceção: eles leem a conta de outro titular apontando para ela na query string.
  */
  static getOwnerQuery () {
    if (!Auth.isAdmin ()) return ''

    return `?user_type=Business&user_id=${Auth.getBusinessId ()}`
  }

  static canOpenAccount () {
    return ['business', 'professional'].includes (Auth.getUserType ())
  }

  /*
    Movimentar dinheiro é exclusivo do titular. As rotas de saque e transferência deixam o
    administrador de fora de propósito — mesmo apontando para a conta na query string, ele
    lê mas não movimenta —, então a aba inteira some para ele em vez de oferecer um
    formulário que a API vai recusar.
  */
  static canMoveMoney () {
    return !Auth.isAdmin () && ['business', 'professional'].includes (Auth.getUserType ())
  }

  /*
    Os documentos da abertura também são só do titular: a rota de envio deixa o administrador
    de fora, como as de dinheiro. Ele acompanha a situação de cada documento, mas não recebe
    um botão que a API vai recusar.
  */
  static canSendDocuments () {
    return !Auth.isAdmin () && ['business', 'professional'].includes (Auth.getUserType ())
  }

  static isBusinessOwner () {
    return Auth.getUserType () === 'business'
  }

  /*
    Chave de idempotência de uma tentativa de pagamento. Mesma ideia — e mesma ressalva
    sobre o `randomUUID` exigir contexto seguro — da que o `services/OnlinePayment` usa nas
    cobranças por cartão; os dois domínios são independentes e cada um guarda a sua.
  */
  static newIdempotencyKey () {
    const uuid = typeof window !== 'undefined'
      ? window.crypto?.randomUUID?.()
      : null

    return uuid ?? `${Date.now ()}-${Math.random ().toString (36).slice (2)}`
  }

  /*
    Se a próxima tentativa pode reusar a chave da anterior.

    Falha sem resposta é justamente o caso que a chave existe para cobrir: não dá para saber
    se o pagamento aconteceu, então a repetição precisa ir com a mesma chave para a API
    devolver a transação que já existe em vez de criar outra.

    Recusa com resposta é o contrário — saldo insuficiente, código expirado —, e a próxima
    tentativa é um pagamento novo. Reusar a chave ali devolveria a recusa para sempre.
  */
  static shouldKeepIdempotencyKey (error) {
    return !error?.response
  }
}
